import { Moment } from "moment";
import debug from "../debug";
import { TimelineEvent, BoardState, copyBoard, updateBoard, TimelineEventType, initBoardBuckets } from "./events";
import { Labels } from "../types";

const log = debug("cfd");

const MAX_POINTS = 300;

export function cfd(
    initialBoard: BoardState,
    events: TimelineEvent[],
    columnId: string,
    labels: Labels,
    start: Moment,
    end: Moment,
    subdued?: boolean
): Highcharts.SeriesAreaOptions[] {
    log(`Getting cfd from ${start.format("L")} to ${end.format("L")}`);
    const getSnapshot = (state: BoardState): { [labelId: string]: number } => {
        const itemsPerStatus: { [labelId: string]: number } = {};
        for (const itemId in state.items) {
            const labelId = state.items[itemId][columnId];
            if (labelId !== undefined) {
                itemsPerStatus[labelId] = itemsPerStatus[labelId] ? itemsPerStatus[labelId] + 1 : 1;
            }
        }
        for (const labelId in labels.names) {
            if (itemsPerStatus[labelId] === undefined) {
                itemsPerStatus[labelId] = 0;
            }
        }
        return itemsPerStatus;
    };

    const resolutionMs = end.diff(start, "ms") / MAX_POINTS;
    const board = copyBoard(initialBoard); // working board, modified as we traverse events

    const series: { [labelId: string]: Highcharts.SeriesAreaOptions } = {};

    for (const labelId in labels.names) {
        const style = labels.labelsColors[labelId];
        series[labelId] = {
            type: "area",
            id: labelId,
            name: labels.names[labelId],
            data: [],
            color: subdued ? style.colorInactive : style.color,
            fillOpacity: 0.75,
            lineColor: style.border,
            lineWidth: subdued ? 0 : 2
        };
    }

    // move board to start date and save the state of buckets at start
    initBoardBuckets(labels.orderedIds, board, columnId);
    let { eventIdx } = updateBoard(board, events, 0, start, columnId);
    const buckets = getSnapshot(board);

    let lastSavedSnapshot: number | null = null;

    const saveSnapshot = (atMs: number): void => {
        const copyLastSnapshot = (atMs: number): void => {
            for (const labelId in buckets) {
                if (series[labelId]) {
                    const data = series[labelId].data!;
                    const lastEntry: [number, number] = data[data.length - 1] as any;
                    data.push([atMs, lastEntry[1]]);
                }
            }
        };

        // insert snapshot before this point if the gap too large to avoid triangular looking shapes on a graph
        if (lastSavedSnapshot !== null && atMs - lastSavedSnapshot > 2 * resolutionMs) {
            copyLastSnapshot(Math.floor(atMs - resolutionMs));
        }

        for (const labelId in buckets) {
            if (series[labelId]) {
                series[labelId].data!.push([atMs, buckets[labelId]]);
            }
        }
        lastSavedSnapshot = atMs;
    };

    saveSnapshot(start.valueOf());

    let nextSnapshot: number = start.valueOf() + resolutionMs;

    // progress events until end date, keep buckets up to date
    let e: TimelineEvent | null = null;
    while (eventIdx < events.length && events[eventIdx].date.isSameOrBefore(end)) {
        e = events[eventIdx];

        if (e.date.valueOf() > nextSnapshot) {
            saveSnapshot(Math.floor(nextSnapshot));

            // move next planned snapshot to after this event
            do {
                nextSnapshot += resolutionMs;
            } while (nextSnapshot <= e.date.valueOf());
        }

        if (e.column === columnId) {
            buckets[e.value] += 1;
            if (e.event === TimelineEventType.UPDATE) {
                const prevBucket = board.items[e.item][columnId];
                if (prevBucket !== undefined) {
                    buckets[prevBucket] -= 1;
                }
                board.items[e.item][columnId] = e.value;
            } else if (e.event === TimelineEventType.CREATE) {
                board.items[e.item] = { [columnId]: e.value };
            }
        }
        eventIdx += 1;
    }

    // flush all unsaved buckets after the last event in case there's too much gap until end date
    // don't save if we're close to the end
    if (e && end.valueOf() - e.date.valueOf() > resolutionMs) {
        saveSnapshot(e.date.valueOf());
    }

    // add snapshot at the end
    saveSnapshot(end.valueOf());

    // sort series according to label sorting
    const seriesList: Highcharts.SeriesAreaOptions[] = [];
    labels.orderedIds.forEach((labelId) => {
        if (series[labelId]) {
            seriesList.push(series[labelId]);
        }
    });

    return seriesList;
}
